<template>
<div class="admin-page wide">
<!-- Loading overlay -->
<transition name="fading">
    <Loading v-if="isLoading"/>
</transition>

<div class="page-hero">
    <div>
        <p class="page-kicker">Artifact Management</p>
        <h1 class="page-title"><AmphoraIcon :size="26" /> Artifact masks</h1>
        <p class="page-subtitle">Upload, filter, preview, download, and deactivate artifact masks by organization.</p>
    </div>
    <button 
        class="ui-button primary" 
        @click="showUploadModal = true"
    >
        <i class="fas fa-plus"></i> Upload mask
    </button>
</div>

<div class="stats-grid">
    <div class="stat-card">
        <span class="stat-label">Active masks</span>
        <strong class="stat-value">{{ filteredArtifactMasks.length }}</strong>
    </div>
    <div class="stat-card">
        <span class="stat-label">Organizations</span>
        <strong class="stat-value">{{ organizations.length }}</strong>
    </div>
    <div class="stat-card">
        <span class="stat-label">Rows per page</span>
        <strong class="stat-value">{{ rowsPerPage }}</strong>
    </div>
</div>

<!-- ================ ARTIFACT MASKS CONTAINER ================ -->
<div class="artifact-shell">
    <!-- Filter Section -->
    <div class="filter-section mb-4">
    <div class="card filter-card">
        <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="filter-title m-0">Filters</h5>
            <button 
            class="btn-reset" 
            @click="resetFilters"
            title="Reset all filters"
            >
            <i class="fas fa-undo-alt"></i> Reset
            </button>
        </div>
        
        <div class="row g-3">
            <div class="col-md-6 col-lg-3">
            <label for="organizationFilter" class="form-label">Organization</label>
            <select 
                id="organizationFilter" 
                v-model="filters.organizationUuid" 
                class="form-select"
            >
                <option value="">All Organizations</option>
                <option 
                v-for="org in organizations" 
                :key="org.OrganizationUuid" 
                :value="org.OrganizationUuid"
                >
                {{ org.OrganizationName }}
                </option>
            </select>
            </div>
            
            <div class="col-md-6 col-lg-3">
            <label for="maskUuidFilter" class="form-label">Mask UUID</label>
            <input 
                type="text" 
                id="maskUuidFilter" 
                v-model="filters.maskUuid" 
                class="form-control" 
                placeholder="Enter mask UUID"
            />
            </div>
            
            <div class="col-md-6 col-lg-3">
            <label for="dateFromFilter" class="form-label">Date From</label>
            <input 
                type="date" 
                id="dateFromFilter" 
                v-model="filters.dateFrom" 
                class="form-control"
            />
            </div>
            
            <div class="col-md-6 col-lg-3">
            <label for="dateToFilter" class="form-label">Date To</label>
            <input 
                type="date" 
                id="dateToFilter" 
                v-model="filters.dateTo" 
                class="form-control"
            />
            </div>
        </div>
        
        <div class="d-flex justify-content-end mt-3">
            <button 
            class="btn-apply" 
            @click="applyFilters"
            >
            <i class="fas fa-filter"></i> Apply Filters
            </button>
        </div>
        </div>
    </div>
    </div>

    <!-- Action Bar -->
    <!-- Artifacts Table -->
    <div class="card data-card">
    <div class="card-body p-0">
        <div v-if="filteredArtifactMasks.length === 0 && !isLoading" class="empty-state">
        <div class="empty-icon">
            <i class="fas fa-search"></i>
        </div>
        <h5>No artifact masks found</h5>
        <p>Try adjusting your filters or upload a new artifact mask</p>
        </div>

        <div v-else>
        <div class="table-responsive">
            <table class="table artifact-table">
            <thead>
                <tr>
                <th>Preview</th>
                <th>Mask UUID</th>
                <th>Organization</th>
                <th>File Name</th>
                <th>Mask Type</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="mask in paginatedArtifactMasks" :key="mask.ArtifactMaskUuid" class="artifact-row">
                <td data-label="Preview">
                    <div class="preview-container">
                    <img 
                        :src="mask.DownloadUrl" 
                        alt="Mask Preview" 
                        class="mask-preview"
                        @click="openImagePreview(mask.DownloadUrl, mask.FileName)"
                    />
                    </div>
                </td>
                <td data-label="Mask UUID">
                    <div class="uuid-container">
                    <span class="mask-uuid">{{ truncateUuid(mask.ArtifactMaskUuid) }}</span>
                    <button 
                        class="btn-copy" 
                        @click="copyToClipboard(mask.ArtifactMaskUuid)"
                        title="Copy UUID"
                    >
                        <i class="fas fa-copy"></i>
                    </button>
                    </div>
                </td>
                <td data-label="Organization">
                    <span class="org-name">{{ getOrganizationName(mask.OrganizationUuid) }}</span>
                </td>
                <td data-label="File Name">
                    <span class="file-name">{{ mask.FileName }}</span>
                </td>
                <td data-label="Mask Type">
                    <span class="badge mask-type">{{ mask.MaskType }}</span>
                </td>
                <td data-label="Status">
                    <span 
                    class="badge"
                    :class="mask.IsActive ? 'status-active' : 'status-inactive'"
                    >
                    {{ mask.IsActive ? 'Active' : 'Inactive' }}
                    </span>
                </td>
                <td data-label="Created At">
                    <div class="date-container">
                    <span class="date">{{ formatDate(mask.CreatedAt) }}</span>
                    </div>
                </td>
                <td data-label="Actions">
                    <div class="actions-container">
                    <button 
                        v-if="mask.IsActive"
                        class="btn-action btn-deactivate"
                        @click="confirmDeactivate(mask)"
                        title="Deactivate Mask"
                    >
                        <i class="fas fa-ban"></i>
                    </button>
                    <button 
                        class="btn-action btn-download"
                        @click="downloadImage(mask.DownloadUrl, mask.FileName)"
                        title="Download Mask"
                    >
                        <i class="fas fa-download"></i>
                    </button>
                    </div>
                </td>
                </tr>
            </tbody>
            </table>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-container">
            <div class="rows-per-page">
            <span>Rows per page:</span>
            <select v-model="rowsPerPage" @change="updatePagination">
                <option v-for="option in rowsPerPageOptions" :key="option" :value="option">
                {{ option }}
                </option>
            </select>
            </div>
            
            <div class="pagination-info">
            <span>{{ paginationRangeText }}</span>
            </div>
            
            <div class="pagination-controls">
            <button 
                class="btn-page" 
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
            >
                <i class="fas fa-chevron-left"></i>
            </button>
            
            <div class="page-numbers">
                <button 
                v-for="page in displayedPageNumbers" 
                :key="page"
                class="btn-page-number"
                :class="{ active: currentPage === page }"
                @click="goToPage(page)"
                >
                {{ page }}
                </button>
            </div>
            
            <button 
                class="btn-page" 
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
            >
                <i class="fas fa-chevron-right"></i>
            </button>
            </div>
        </div>
        </div>
    </div>
    </div>
</div>
</div>

<!-- Image Preview Modal -->
<div class="modal fade" id="imagePreviewModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
    <div class="modal-content">
        <div class="modal-header">
        <h5 class="modal-title">{{ previewImageTitle }}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-center">
        <img :src="previewImageUrl" alt="Mask Preview" class="img-fluid preview-image" />
        </div>
        <div class="modal-footer">
        <button 
            type="button" 
            class="btn-download-preview"
            @click="downloadImage(previewImageUrl, previewImageTitle)"
        >
            <i class="fas fa-download"></i> Download
        </button>
        <button 
            type="button" 
            class="btn-close-preview"
            data-bs-dismiss="modal"
        >
            Close
        </button>
        </div>
    </div>
    </div>
</div>

<!-- Confirmation Modal - Using Bootstrap modal instead of custom Modal component -->
<div class="modal fade" id="confirmDeactivateModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
        <div class="modal-header">
        <h5 class="modal-title">Confirm Deactivation</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
        <p>Are you sure you want to deactivate this artifact mask?</p>
        <p class="text-danger">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
        <button 
            type="button" 
            class="btn-cancel" 
            data-bs-dismiss="modal"
        >
            Cancel
        </button>
        <button 
            type="button" 
            class="btn-confirm" 
            @click="deactivateMask"
        >
            Deactivate
        </button>
        </div>
    </div>
    </div>
</div>

<!-- Toast Notification -->
<div class="toast-container position-fixed bottom-0 end-0 p-3">
    <div 
    id="copyToast" 
    class="toast align-items-center text-white bg-success border-0" 
    role="alert" 
    aria-live="assertive" 
    aria-atomic="true"
    >
    <div class="d-flex">
        <div class="toast-body">
        <i class="fas fa-check-circle me-2"></i> UUID copied to clipboard!
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
    </div>
    
    <!-- Success Toast -->
    <div 
    id="successToast" 
    class="toast align-items-center text-white bg-success border-0" 
    role="alert" 
    aria-live="assertive" 
    aria-atomic="true"
    >
    <div class="d-flex">
        <div class="toast-body">
        <i class="fas fa-check-circle me-2"></i> Mask deactivated successfully!
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
    </div>
    
    <!-- Error Toast -->
    <div 
    id="errorToast" 
    class="toast align-items-center text-white bg-danger border-0" 
    role="alert" 
    aria-live="assertive" 
    aria-atomic="true"
    >
    <div class="d-flex">
        <div class="toast-body">
        <i class="fas fa-exclamation-circle me-2"></i> <span id="errorToastMessage">Error occurred</span>
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
    </div>
</div>

<!-- Artifact Upload Modal -->
<ArtifactUploadModal 
    :show="showUploadModal" 
    @close="showUploadModal = false"
    @upload-success="fetchArtifactMasks"
/>
</template>

<script>
import Loading from "@/components/Loading.vue";
import { getAllOrg } from "@/services/orgServices";
import { getArtifactMasks, deactivateArtifactMask } from "@/services/artifactServices";
import { AmphoraIcon } from 'lucide-vue-next';
import ArtifactUploadModal from './components/ArtifactUploadModal.vue';

export default {
name: 'ArtifactsList',
components: {
    Loading,
    ArtifactUploadModal,
    AmphoraIcon
},
data() {
    return {
    isLoading: false,
    organizations: [],
    artifactMasks: [],
    filters: {
        organizationUuid: '',
        maskUuid: '',
        dateFrom: '',
        dateTo: ''
    },
    previewImageUrl: '',
    previewImageTitle: '',
    selectedMask: null,
    showUploadModal: false,
    
    // Bootstrap modals
    imagePreviewModal: null,
    confirmDeactivateModal: null,
    
    // Toasts
    copyToast: null,
    successToast: null,
    errorToast: null,
    
    // Pagination
    currentPage: 1,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 15, 20]
    };
},
computed: {
filteredArtifactMasks() {
    let filtered = [...this.artifactMasks];
    
    // Only show active masks
    filtered = filtered.filter(mask => mask.IsActive === true);
    
    // Filter by date if date filters are set
    if (this.filters.dateFrom) {
    const fromDate = new Date(this.filters.dateFrom);
    filtered = filtered.filter(mask => {
        const createdDate = new Date(mask.CreatedAt);
        return createdDate >= fromDate;
    });
    }
    
    if (this.filters.dateTo) {
    const toDate = new Date(this.filters.dateTo);
    // Set time to end of day
    toDate.setHours(23, 59, 59, 999);
    filtered = filtered.filter(mask => {
        const createdDate = new Date(mask.CreatedAt);
        return createdDate <= toDate;
    });
    }
    
    return filtered;
},

totalPages() {
    return Math.ceil(this.filteredArtifactMasks.length / this.rowsPerPage);
},

paginatedArtifactMasks() {
    const start = (this.currentPage - 1) * this.rowsPerPage;
    const end = start + this.rowsPerPage;
    return this.filteredArtifactMasks.slice(start, end);
},

paginationRangeText() {
    const start = (this.currentPage - 1) * this.rowsPerPage + 1;
    const end = Math.min(start + this.rowsPerPage - 1, this.filteredArtifactMasks.length);
    return `${start}-${end} of ${this.filteredArtifactMasks.length}`;
},

displayedPageNumbers() {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages) {
    // Show all pages if total pages is less than or equal to max visible pages
    for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
    }
    } else {
    // Always show first page
    pages.push(1);
    
    let startPage = Math.max(2, this.currentPage - 1);
    let endPage = Math.min(this.totalPages - 1, this.currentPage + 1);
    
    // Adjust start and end page to ensure we show maxVisiblePages - 2 pages (excluding first and last)
    const visiblePagesCount = endPage - startPage + 1;
    if (visiblePagesCount < maxVisiblePages - 2) {
        if (startPage === 2) {
        endPage = Math.min(this.totalPages - 1, endPage + (maxVisiblePages - 2 - visiblePagesCount));
        } else if (endPage === this.totalPages - 1) {
        startPage = Math.max(2, startPage - (maxVisiblePages - 2 - visiblePagesCount));
        }
    }
    
    // Add ellipsis if needed
    if (startPage > 2) {
        pages.push('...');
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < this.totalPages - 1) {
        pages.push('...');
    }
    
    // Always show last page
    if (this.totalPages > 1) {
        pages.push(this.totalPages);
    }
    }
    
    return pages;
}
},
created() {
this.fetchOrganizations();
this.fetchArtifactMasks();
},
mounted() {
// Initialize Bootstrap modals and toasts if Bootstrap is available
if (typeof bootstrap !== 'undefined') {
    this.imagePreviewModal = new bootstrap.Modal(document.getElementById('imagePreviewModal'));
    this.confirmDeactivateModal = new bootstrap.Modal(document.getElementById('confirmDeactivateModal'));
    this.copyToast = new bootstrap.Toast(document.getElementById('copyToast'));
    this.successToast = new bootstrap.Toast(document.getElementById('successToast'));
    this.errorToast = new bootstrap.Toast(document.getElementById('errorToast'));
}
},
methods: {
async fetchOrganizations() {
    try {
    const result = await getAllOrg();
    if (result.data) {
        this.organizations = result.data;
    }
    } catch (error) {
    console.error('Error fetching organizations:', error);
    this.showErrorToast('Failed to fetch organizations');
    }
},

async fetchArtifactMasks() {
    this.isLoading = true;
    try {
    const result = await getArtifactMasks(
        this.filters.organizationUuid,
        this.filters.maskUuid
    );
    
    if (result.data) {
        this.artifactMasks = result.data;
        // Reset to first page when data changes
        this.currentPage = 1;
    } else {
        this.artifactMasks = [];
    }
    } catch (error) {
    console.error('Error fetching artifact masks:', error);
    this.showErrorToast('Failed to fetch artifact masks');
    this.artifactMasks = [];
    } finally {
    this.isLoading = false;
    }
},

applyFilters() {
    this.fetchArtifactMasks();
},

resetFilters() {
    this.filters = {
    organizationUuid: '',
    maskUuid: '',
    dateFrom: '',
    dateTo: ''
    };
    this.fetchArtifactMasks();
},

getOrganizationName(uuid) {
    const org = this.organizations.find(org => org.OrganizationUuid === uuid);
    return org ? org.OrganizationName : uuid;
},

formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
    }).format(date);
},

truncateUuid(uuid) {
    if (!uuid) return '';
    return uuid.substring(0, 8) + '...' + uuid.substring(uuid.length - 4);
},

copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
    if (this.copyToast) {
        this.copyToast.show();
    }
    }).catch(err => {
    console.error('Failed to copy text: ', err);
    this.showErrorToast('Failed to copy to clipboard');
    });
},

openImagePreview(imageUrl, fileName) {
    this.previewImageUrl = imageUrl;
    this.previewImageTitle = fileName || 'Artifact Mask Preview';
    if (this.imagePreviewModal) {
    this.imagePreviewModal.show();
    }
},

downloadImage(url, fileName) {
  // Open in a new tab
  window.open(url, '_blank');
  
  // Show a toast with instructions
  this.showInfoToast('Image opened in a new tab. Right-click and select "Save image as..." to download.');
},

// Add a new info toast method
showInfoToast(message) {
  if (typeof bootstrap !== 'undefined') {
    const toastEl = document.getElementById('infoToast') || this.createInfoToast();
    const toastBody = toastEl.querySelector('.toast-body');
    if (toastBody) {
      toastBody.textContent = message;
    }
    const toast = new bootstrap.Toast(toastEl);
    toast.show();
  }
},

createInfoToast() {
  const toastContainer = document.querySelector('.toast-container');
  if (!toastContainer) return null;
  
  const toastEl = document.createElement('div');
  toastEl.id = 'infoToast';
  toastEl.className = 'toast align-items-center text-white bg-info border-0';
  toastEl.setAttribute('role', 'alert');
  toastEl.setAttribute('aria-live', 'assertive');
  toastEl.setAttribute('aria-atomic', 'true');
  
  toastEl.innerHTML = `
    <div class="d-flex">
      <div class="toast-body">
        <i class="fas fa-info-circle me-2"></i> <span></span>
      </div>
      <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
  `;
  
  toastContainer.appendChild(toastEl);
  return toastEl;
},

confirmDeactivate(mask) {
    this.selectedMask = mask;
    if (this.confirmDeactivateModal) {
    this.confirmDeactivateModal.show();
    }
},

async deactivateMask() {
    if (!this.selectedMask) return;
    
    this.isLoading = true;
    try {
    // Using the deactivateArtifactMask function from artifactServices.js
    await deactivateArtifactMask(this.selectedMask.ArtifactMaskUuid);
    
    // Hide the modal
    if (this.confirmDeactivateModal) {
        this.confirmDeactivateModal.hide();
    }
    
    // Show success toast
    if (this.successToast) {
        this.successToast.show();
    }
    
    // Refresh the list after deactivation
    await this.fetchArtifactMasks();
    } catch (error) {
    console.error('Error deactivating mask:', error);
    this.showErrorToast('Failed to deactivate mask');
    } finally {
    this.isLoading = false;
    this.selectedMask = null;
    }
},

showErrorToast(message) {
    if (this.errorToast) {
    const errorMessageElement = document.getElementById('errorToastMessage');
    if (errorMessageElement) {
        errorMessageElement.textContent = message || 'An error occurred';
    }
    this.errorToast.show();
    }
},

// Pagination methods
updatePagination() {
    // Reset to first page when rows per page changes
    this.currentPage = 1;
},

goToPage(page) {
    if (page >= 1 && page <= this.totalPages) {
    this.currentPage = page;
    }
}
}
};
</script>

<style scoped>
/* Modern, minimalist styling */
:root {
--primary-color: #4361ee;
--primary-hover: #3a56d4;
--secondary-color: #f8f9fa;
--success-color: #2ecc71;
--danger-color: #e74c3c;
--warning-color: #f39c12;
--text-primary: #2d3748;
--text-secondary: #718096;
--border-color: #e2e8f0;
--card-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
--transition-speed: 0.2s;
}

/* Cards */
.filter-card, .data-card {
border-radius: 12px;
border: none;
box-shadow: var(--card-shadow);
overflow: hidden;
transition: all var(--transition-speed);
}

.filter-card:hover, .data-card:hover {
box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

.filter-title {
color: var(--text-primary);
font-weight: 600;
font-size: 1.1rem;
}

/* Form elements */
.form-label {
font-weight: 500;
font-size: 0.85rem;
color: var(--text-secondary);
margin-bottom: 0.3rem;
}

.form-control, .form-select {
border-radius: 8px;
border: 1px solid var(--border-color);
padding: 0.6rem 1rem;
font-size: 0.9rem;
transition: all var(--transition-speed);
}

.form-control:focus, .form-select:focus {
border-color: var(--primary-color);
box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
}

/* Buttons */
.btn-reset, .btn-apply, .btn-upload, .btn-action, 
.btn-page, .btn-page-number, .btn-download-preview, .btn-close-preview,
.btn-cancel, .btn-confirm {
border-radius: 8px;
font-weight: 500;
font-size: 0.9rem;
padding: 0.6rem 1.2rem;
transition: all var(--transition-speed);
border: none;
cursor: pointer;
display: inline-flex;
align-items: center;
justify-content: center;
gap: 0.5rem;
}

.btn-reset {
background-color: var(--secondary-color);
color: var(--text-secondary);
}

.btn-reset:hover {
background-color: #e9ecef;
}

.btn-apply {
background-color: var(--primary-color);
color: white;
}

.btn-apply:hover {
background-color: var(--primary-hover);
transform: translateY(-1px);
}

.btn-upload {
background-color: var(--primary-color);
color: white;
padding: 0.7rem 1.4rem;
}

.btn-upload:hover {
background-color: var(--primary-hover);
transform: translateY(-1px);
}

.btn-action {
width: 36px;
height: 36px;
padding: 0;
border-radius: 6px;
}

.btn-deactivate {
background-color: rgba(231, 76, 60, 0.1);
color: #e74c3c;
}

.btn-deactivate:hover {
background-color: #e74c3c;
color: white;
}

.btn-download {
background-color: rgba(67, 97, 238, 0.1);
color: var(--primary-color);
}

.btn-download:hover {
background-color: var(--primary-color);
color: white;
}

.btn-copy {
background: none;
border: none;
color: var(--text-secondary);
cursor: pointer;
padding: 0.2rem;
font-size: 0.8rem;
}

.btn-copy:hover {
color: var(--primary-color);
}

/* Action bar */
.action-bar {
display: flex;
justify-content: space-between;
align-items: center;
}

.stats-badge {
background-color: var(--secondary-color);
color: var(--text-secondary);
padding: 0.5rem 1rem;
border-radius: 20px;
font-size: 0.9rem;
font-weight: 500;
}

/* Table styling */
.artifact-table {
margin-bottom: 0;
width: 100%;
border-collapse: separate;
border-spacing: 0;
}

.artifact-table th {
background-color: #f8fafc;
color: var(--text-secondary);
font-weight: 600;
font-size: 0.85rem;
text-transform: uppercase;
letter-spacing: 0.5px;
padding: 1rem;
border-bottom: 1px solid var(--border-color);
}

.artifact-table td {
padding: 1rem;
vertical-align: middle;
border-bottom: 1px solid var(--border-color);
color: var(--text-primary);
font-size: 0.9rem;
}

.artifact-row {
transition: background-color var(--transition-speed);
}

.artifact-row:hover {
background-color: #f8fafc;
}

/* Table content styling */
.preview-container {
display: flex;
justify-content: center;
}

.mask-preview {
width: 50px;
height: 50px;
object-fit: cover;
border-radius: 8px;
cursor: pointer;
transition: transform var(--transition-speed);
border: 1px solid var(--border-color);
}

.mask-preview:hover {
transform: scale(1.1);
}

.uuid-container {
display: flex;
align-items: center;
gap: 0.5rem;
}

.mask-uuid {
font-family: monospace;
font-size: 0.85rem;
color: var(--text-secondary);
}

.org-name, .file-name {
font-weight: 500;
}

.badge {
font-weight: 500;
padding: 0.4em 0.8em;
border-radius: 6px;
font-size: 0.75rem;
}

.mask-type {
background-color: rgba(67, 97, 238, 0.1);
color: var(--primary-color);
}

.status-active {
background-color: rgba(46, 204, 113, 0.1);
color: var(--success-color);
}

.status-inactive {
background-color: rgba(231, 76, 60, 0.1);
color: #e74c3c;
}

.date-container {
white-space: nowrap;
}

.actions-container {
display: flex;
gap: 0.5rem;
justify-content: flex-end;
}

/* Empty state */
.empty-state {
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
padding: 4rem 2rem;
color: var(--text-secondary);
}

.empty-icon {
font-size: 3rem;
margin-bottom: 1rem;
color: #cbd5e0;
}

.empty-state h5 {
font-weight: 600;
margin-bottom: 0.5rem;
color: var(--text-primary);
}

.empty-state p {
text-align: center;
max-width: 400px;
}

/* Pagination */
.pagination-container {
display: flex;
justify-content: space-between;
align-items: center;
padding: 1rem;
border-top: 1px solid var(--border-color);
background-color: #f8fafc;
}

.rows-per-page {
display: flex;
align-items: center;
gap: 0.5rem;
color: var(--text-secondary);
font-size: 0.85rem;
}

.rows-per-page select {
border: 1px solid var(--border-color);
border-radius: 4px;
padding: 0.3rem 0.5rem;
font-size: 0.85rem;
}

.pagination-info {
color: var(--text-secondary);
font-size: 0.85rem;
}

.pagination-controls {
display: flex;
align-items: center;
gap: 0.25rem;
}

.btn-page {
width: 36px;
height: 36px;
padding: 0;
border-radius: 6px;
background-color: transparent;
color: var(--text-secondary);
}

.btn-page:hover:not(:disabled) {
background-color: rgba(67, 97, 238, 0.1);
color: var(--primary-color);
}

.btn-page:disabled {
opacity: 0.5;
cursor: not-allowed;
}

.page-numbers {
display: flex;
gap: 0.25rem;
}

.btn-page-number {
width: 36px;
height: 36px;
padding: 0;
border-radius: 6px;
background-color: transparent;
color: var(--text-secondary);
}

.btn-page-number:hover {
background-color: rgba(67, 97, 238, 0.1);
color: var(--primary-color);
}

.btn-page-number.active {
background-color: var(--primary-color);
color: white;
}

/* Modal styling */
.modal-content {
border-radius: 12px;
border: none;
overflow: hidden;
}

.modal-header {
background-color: #f8fafc;
border-bottom: 1px solid var(--border-color);
padding: 1rem 1.5rem;
}

.modal-title {
font-weight: 600;
color: var(--text-primary);
}

.modal-body {
padding: 1.5rem;
}

.modal-footer {
border-top: 1px solid var(--border-color);
padding: 1rem 1.5rem;
}

.btn-download-preview {
background-color: var(--primary-color);
color: white;
}

.btn-download-preview:hover {
background-color: var(--primary-hover);
}

.btn-close-preview {
background-color: var(--secondary-color);
color: var(--text-secondary);
}

.btn-close-preview:hover {
background-color: #e9ecef;
}

.preview-image {
max-height: 70vh;
border-radius: 8px;
}

/* Confirmation modal buttons */
.btn-cancel {
background-color: var(--secondary-color);
color: var(--text-secondary);
}

.btn-cancel:hover {
background-color: #e9ecef;
}

.btn-confirm {
background-color: #e74c3c;
color: white;
}

.btn-confirm:hover {
background-color: #c0392b;
}

/* Toast styling */
.toast {
border-radius: 8px;
box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments for mobile */
@media (max-width: 992px) {
.pagination-container {
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
}

.pagination-info {
    width: 100%;
    text-align: center;
    order: -1;
}

.rows-per-page {
    margin-right: auto;
}

.pagination-controls {
    margin-left: auto;
}
}

@media (max-width: 768px) {
/* Improved mobile card view for tables */
.artifact-table {
    border: none;
}

.artifact-table thead {
    display: none;
}

.artifact-table tbody {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    padding: 1rem;
}

.artifact-row {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: var(--card-shadow);
    background-color: white;
    transition: transform var(--transition-speed), box-shadow var(--transition-speed);
}

.artifact-row:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.artifact-table td {
    display: block;
    padding: 0.75rem;
    border-bottom: 1px solid var(--border-color);
    text-align: left;
}

.artifact-table td:last-child {
    border-bottom: none;
}

.artifact-table td::before {
    content: attr(data-label);
    display: block;
    font-weight: 600;
    font-size: 0.8rem;
    color: var(--text-secondary);
    margin-bottom: 0.3rem;
}

/* Special styling for preview in mobile view */
td[data-label="Preview"] {
    background-color: #f8fafc;
    padding: 1rem;
    text-align: center;
    border-bottom: 1px solid var(--border-color);
}

.preview-container {
    justify-content: center;
}

.mask-preview {
    width: 80px;
    height: 80px;
}

/* Adjust containers in mobile view */
.uuid-container, .actions-container {
    justify-content: flex-start;
}

/* Action bar in mobile view */
.action-bar {
    flex-direction: column;
    gap: 1rem;
}

.btn-upload {
    width: 100%;
}

.stats-badge {
    align-self: center;
}

/* Filter section in mobile view */
.filter-card .card-body {
    padding: 1rem;
}

.filter-card .d-flex {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
}

.filter-card .btn-reset {
    align-self: flex-end;
}

.filter-card .btn-apply {
    width: 100%;
    margin-top: 0.5rem;
}

/* Pagination in mobile view */
.pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
}

.rows-per-page, .pagination-info, .pagination-controls {
    width: 100%;
    justify-content: center;
}

/* Improved mobile card layout */
.artifact-table td.mobile-card-header {
    background-color: #f8fafc;
    border-bottom: 1px solid var(--border-color);
    padding: 1rem;
}

.mobile-card-content {
    padding: 0;
}

.mobile-card-footer {
    background-color: #f8fafc;
    padding: 1rem;
    border-top: 1px solid var(--border-color);
}

/* Mobile-specific badge styling */
.mobile-badge-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}
}

/* Additional mobile optimizations */
@media (max-width: 576px) {
.artifact-table tbody {
    grid-template-columns: 1fr;
}

.pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
}

.page-numbers {
    order: 2;
    margin-top: 0.5rem;
    width: 100%;
    justify-content: center;
}

.btn-page {
    order: 1;
}

.btn-page:last-child {
    order: 3;
}
}

.page-title {
display: flex;
align-items: center;
gap: 10px;
}

.artifact-shell {
width: 100%;
}

.filter-card,
.data-card {
border: 1px solid var(--ui-border) !important;
border-radius: var(--ui-radius) !important;
box-shadow: var(--ui-shadow) !important;
background: #ffffff !important;
}

.filter-card:hover,
.data-card:hover {
transform: none !important;
box-shadow: var(--ui-shadow) !important;
}

.filter-title {
color: var(--ui-text) !important;
font-weight: 650 !important;
}

.form-label {
color: var(--ui-muted) !important;
font-size: 0.84rem !important;
font-weight: 600 !important;
margin-bottom: 8px !important;
}

.form-select,
.form-control,
.rows-per-page select {
min-height: 40px !important;
border: 1px solid var(--ui-border) !important;
border-radius: 9px !important;
background: #fff !important;
color: var(--ui-text) !important;
box-shadow: none !important;
}

.form-select:focus,
.form-control:focus {
border-color: var(--primary-color) !important;
box-shadow: 0 0 0 4px var(--ui-ring) !important;
}

.btn-reset,
.btn-apply,
.btn-upload,
.btn-download-preview,
.btn-confirm {
min-height: 38px !important;
border-radius: 9px !important;
font-weight: 650 !important;
box-shadow: none !important;
}

.btn-apply,
.btn-upload,
.btn-download-preview {
background: var(--primary-color) !important;
color: #ffffff !important;
}

.btn-reset,
.btn-close-preview,
.btn-cancel {
background: #ffffff !important;
color: var(--ui-text) !important;
border: 1px solid var(--ui-border-strong) !important;
}

.stats-badge {
display: none !important;
}

.artifact-table {
margin-bottom: 0 !important;
}

.artifact-table th {
background: #f8fafc !important;
color: var(--ui-muted) !important;
font-size: 0.72rem !important;
font-weight: 650 !important;
letter-spacing: 0.06em !important;
text-transform: uppercase !important;
border-bottom: 1px solid var(--ui-border) !important;
}

.artifact-table td {
color: var(--ui-text) !important;
border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.artifact-table tbody tr:hover {
background: #f8fafc !important;
}

.mask-preview {
border-radius: 10px !important;
border: 1px solid var(--ui-border) !important;
}

.badge {
border-radius: 999px !important;
font-weight: 650 !important;
}

.status-active {
background: #eafaf2 !important;
color: #167147 !important;
}

.status-inactive {
background: #fff5f4 !important;
color: var(--error-color) !important;
}

.mask-type {
background: #f8fafc !important;
color: #334155 !important;
}

.pagination-container {
background: #f8fafc !important;
border-top: 1px solid var(--ui-border) !important;
}
</style>
  

      
  
